import { useCallback, useEffect, useRef, useState } from "react";
import { apiAssetUrl, apiGet, apiPatch, apiPost, prettifyApiError } from "@/shared/api";
import type { EditablePackage } from "@/features/ordini/packagesEditing";

export type AddressForm = {
  company: string;
  address: string;
  city: string;
  postcode: string;
  state_iso: string;
  country_iso: string;
  name: string;
  surname: string;
  phone: string;
  email: string;
};

export type ReturnItem = {
  sku: string;
  descrizione: string;
  quantita: number;
};

export type ReturnPackage = EditablePackage;

export type ReturnKind = "reso" | "sostituzione";

export type ReturnStatus =
  // Sostituzione registrata SENZA etichetta: la cliente chiama, i colli e le
  // misure arrivano solo col pronto merce del fornitore. L'etichetta si emette
  // dopo con ``emettiEtichettaReso``.
  "richiesta" | "etichetta_pronta" | "in_transito" | "consegnato" | "chiuso" | "annullato";

export type ReturnRecord = {
  id: number;
  kind: ReturnKind;
  status: ReturnStatus;
  order_reference: string;
  sender: Partial<AddressForm>;
  recipient: Partial<AddressForm>;
  sender_slot: string;
  recipient_slot: string;
  items: ReturnItem[];
  packages: ReturnPackage[];
  contenuto: string;
  tracking_numbers: string[];
  tracking_url: string | null;
  /** ``SDA`` o ``BRT``: SDA consegna solo in Italia, all'estero parte BRT.
   *  Assente sui backend vecchi = SDA. */
  carrier?: string;
  label_path: string | null;
  /** ⚠️ Il gate del bottone «Etichetta» è QUESTO, non ``label_path``: un record
   *  coi soli byte in ``label_pdf`` (migr 0035) ha ``label_path`` nullo e
   *  l'etichetta c'è. Opzionale solo per i fixture legacy: il backend lo manda
   *  sempre. */
  has_label?: boolean;
  /** Id numerico dell'ordine collegato (``null`` se la reference non aggancia
   *  nessun ordine locale). */
  order_id?: number | null;
  /** Rif. interno stampato sull'etichetta (es. ``R-1234``). */
  num_rif_interno?: string;
  note: string | null;
  created_at: string;
};

export type ReturnPrefill = {
  kind: ReturnKind;
  order_reference: string;
  sender: AddressForm;
  recipient: AddressForm;
  sender_slot: string;
  recipient_slot: string;
  items: ReturnItem[];
  packages: ReturnPackage[];
};

/** L'ordine di una pratica, in sola lettura (redesign Resi 27/10/2026):
 *  canale, cliente, data, totale e prodotti senza aprire l'ordine. */
export type OrdineCollegato = {
  id: number;
  reference: string;
  channel: string;
  external_source: string;
  status: string;
  customer_name: string;
  created_at_poleepo: string | null;
  total_amount: string;
  currency: string;
  items: {
    id: number;
    sku: string;
    title: string;
    quantity: number;
    unit_price: string;
    image_url: string;
  }[];
};

export type ReturnsListResponse = {
  items: ReturnRecord[];
  total: number;
  counts: Record<string, number>;
  /** ``order_reference`` -> ordine; assente sui backend vecchi o senza ordine. */
  ordini?: Record<string, OrdineCollegato>;
};

export type CreateReturnBody = {
  kind: ReturnKind;
  order_reference: string;
  sender: AddressForm;
  recipient: AddressForm;
  sender_slot: string;
  recipient_slot: string;
  items: ReturnItem[];
  packages: ReturnPackage[];
  accessori?: Record<string, boolean>;
  note?: string;
  label_format?: string;
  /** ``false`` = registra solo la richiesta, NESSUNA chiamata SDA e nessuna
   *  etichetta bruciata. Default backend: ``true`` (comportamento storico). */
  emetti_etichetta?: boolean;
};

export async function fetchInternalAddresses(): Promise<Record<string, AddressForm>> {
  return apiGet<Record<string, AddressForm>>("/api/returns/internal-addresses");
}

/**
 * Scarica il PDF dell'etichetta reso/sostituzione come ``File`` (per allegarlo a un thread
 * Messaggi via ``uploadThreadAttachments``). Usa ``apiAssetUrl`` (URL assoluto)
 * così funziona sia su desktop (backend locale 8766) sia su web (om.bscom.it).
 */
export async function fetchReturnLabelFile(
  returnId: number,
  orderRef: string,
  kind: ReturnKind = "reso",
): Promise<File> {
  const url = apiAssetUrl(`/api/returns/${returnId}/label`);
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`[/api/returns/${returnId}/label] ${res.status}`);
  }
  const blob = await res.blob();
  const prefix = kind === "sostituzione" ? "sost" : "reso";
  return new File([blob], `${prefix}_${orderRef}.pdf`, { type: "application/pdf" });
}

export async function createReturn(body: CreateReturnBody): Promise<ReturnRecord> {
  return apiPost<ReturnRecord>("/api/returns", body);
}

/** Dati della richiesta di sostituzione presa al telefono: tutto tranne
 *  ``kind``, ``packages`` ed ``emetti_etichetta``, che li mette la funzione. */
export type RichiestaSostituzioneBody = Omit<
  CreateReturnBody,
  "kind" | "packages" | "emetti_etichetta" | "label_format"
>;

/** Sostituzione in DUE TEMPI, primo tempo: registra la richiesta e basta.
 *
 * Serve perché Davide prende la richiesta al telefono, ma colli e misure
 * arrivano col pronto merce del fornitore: senza colli SDA non emette, e
 * un'etichetta emessa oggi con misure inventate è una spedizione vera
 * sbagliata. Il record nasce in stato ``richiesta``; l'etichetta la emette
 * dopo ``emettiEtichettaReso``.
 *
 * ⚠️ ``packages: []`` forzato QUI di proposito: il prefill dell'ordine
 * propone il placeholder ``[{kg: 0}]``, e ``kg > 0`` resta obbligatorio lato
 * API — inoltrarlo darebbe 422 su una richiesta che non ha colli per
 * definizione. */
export async function creaRichiestaSostituzione(
  body: RichiestaSostituzioneBody,
): Promise<ReturnRecord> {
  return apiPost<ReturnRecord>("/api/returns", {
    ...body,
    kind: "sostituzione",
    packages: [],
    emetti_etichetta: false,
  });
}

export type EmettiEtichettaOptions = {
  accessori?: Record<string, boolean>;
  label_format?: string;
};

/** Secondo tempo: i colli esistono, si emette l'etichetta.
 *  Backend: POST /api/returns/{id}/label → 409 se lo stato non è
 *  ``richiesta`` (l'etichetta si emette una volta sola: la seconda sarebbe
 *  una spedizione vera in più). Almeno un collo, con ``kg > 0``. */
export async function emettiEtichettaReso(
  returnId: number,
  packages: ReturnPackage[],
  options: EmettiEtichettaOptions = {},
): Promise<ReturnRecord> {
  return apiPost<ReturnRecord>(`/api/returns/${returnId}/label`, {
    packages,
    ...(options.accessori ? { accessori: options.accessori } : {}),
    ...(options.label_format ? { label_format: options.label_format } : {}),
  });
}

/** Corregge note, mittente e destinatario di una pratica ancora in stato
 *  ``richiesta``; a ``etichetta_pronta`` solo i telefoni (non stampati per
 *  l'instradamento: il ritiro del reso li chiede). 409 altrove.
 *  Backend: PATCH /api/returns/{id}. */
export async function aggiornaReso(
  returnId: number,
  patch: {
    note?: string;
    sender?: AddressForm;
    recipient?: AddressForm;
    sender_phone?: string;
    recipient_phone?: string;
  },
): Promise<ReturnRecord> {
  return apiPatch<ReturnRecord>(`/api/returns/${returnId}`, patch);
}

/**
 * True se l'errore è il gate idempotenza della create: esiste GIÀ un
 * reso/sostituzione ATTIVO per lo stesso ordine+kind (service.py). Il backend
 * lo segnala con ``ValidationError`` → HTTP 400 (non 409) e un messaggio che
 * NON porta l'id numerico del reso (solo ``num_rif_interno``). Rileviamo quindi
 * dal TESTO — stabile — per attivare la valvola «annulla il precedente e ricrea».
 */
export function isActiveReturnConflict(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /annullalo prima|esiste gi[àa] un \S+ attivo/i.test(msg);
}

export async function cancelReturn(id: number): Promise<ReturnRecord> {
  return apiPost<ReturnRecord>(`/api/returns/${id}/cancel`);
}

export async function fetchReturnPrefill(
  orderRef: string,
  kind: ReturnKind,
): Promise<ReturnPrefill> {
  return apiGet<ReturnPrefill>(
    `/api/orders/${encodeURIComponent(orderRef)}/returns/prefill?kind=${kind}`,
  );
}

/**
 * Resi/sostituzioni (attivi + storici) di un ordine. Contratto condiviso con la
 * pagina ordine; qui la usa anche la valvola anti-deadlock del ReturnDialog per
 * risalire all'id del reso attivo da annullare (l'errore del gate non lo porta).
 */
export async function fetchOrderReturns(
  ref: string,
  signal?: AbortSignal,
): Promise<ReturnRecord[]> {
  // `signal` opzionale: senza, l'AbortController di chi chiama abortiva un
  // fetch a cui non era collegato. L'altro chiamante (la valvola
  // anti-deadlock del ReturnDialog) resta invariato.
  return apiGet<ReturnRecord[]>(`/api/orders/${encodeURIComponent(ref)}/returns`, { signal });
}

export interface UseReturnsResult extends ReturnsListResponse {
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useReturns(filters: {
  kind?: string;
  status?: string;
  q?: string;
}): UseReturnsResult {
  const [data, setData] = useState<ReturnsListResponse>({
    items: [],
    total: 0,
    counts: {},
  });
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Vince l'ultima richiesta PARTITA, non l'ultima ARRIVATA. Senza, cambiando
  // in fretta tab (Tutti/Resi/Sostituzioni) o digitando nella ricerca, la
  // risposta lenta del filtro precedente sovrascriveva lista e contatori di
  // quello scelto e spegneva lo spinner con una fetch ancora in volo.
  // Stessa guardia di useOrders / useBiDashboard / useMailThread.
  const requestSeq = useRef(0);

  const { kind, status, q } = filters;

  const refresh = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    setError(null);

    const qs = new URLSearchParams();
    if (kind) qs.set("kind", kind);
    if (status) qs.set("status", status);
    if (q) qs.set("q", q);

    try {
      const result = await apiGet<ReturnsListResponse>(`/api/returns?${qs.toString()}`);
      if (seq === requestSeq.current) setData(result);
    } catch (err) {
      if (seq === requestSeq.current) setError(prettifyApiError(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [kind, status, q]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return {
    ...data,
    isLoading,
    error,
    refresh,
  };
}
